import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, PartyPopper, UserPlus, Check, Inbox, Mail, UserCog, Network, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  announcement: Megaphone,
  squad_celebration: PartyPopper,
  squad_join_request: UserPlus,
  squad_request_approved: Check,
  squad_removed: Bell,
  agency_invite: Mail,
  agency_member_joined: UserPlus,
  agency_recruiter_joined: UserCog,
  agency_team_head: Network,
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/social/notifications`);
      setItems(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try { await axios.post(`${API}/social/notifications/read`, {}); setUnread(0); setItems((it) => it.map((n) => ({ ...n, read: true }))); } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { try { await axios.post(`${API}/social/notifications/read`, { ids: [n.id] }); } catch { /* ignore */ } }
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }} data-testid="notifications-bell">
      <button data-testid="notifications-bell-btn" onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        style={{ position: "relative", background: "var(--bg-surface-hl)", border: "1px solid var(--border-main)", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: "var(--text-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Bell size={18} />
        {unread > 0 && <span data-testid="notifications-badge" style={{ position: "absolute", top: -2, right: -2, background: "#ef4444", color: "#fff", borderRadius: 999, fontSize: "0.62rem", minWidth: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", fontWeight: 700 }}>{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="card" data-testid="notifications-dropdown" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340, maxHeight: 420, overflowY: "auto", zIndex: 60, padding: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.8rem 1rem", borderBottom: "1px solid var(--border-main)" }}>
            <strong>Notifications</strong>
            {unread > 0 && <button onClick={markAll} data-testid="mark-all-read-btn" style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontSize: "0.8rem" }}>Mark all read</button>}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}><Inbox size={28} style={{ opacity: 0.4 }} /><p style={{ fontSize: "0.85rem" }}>You're all caught up.</p></div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)}
                style={{ width: "100%", textAlign: "left", display: "flex", gap: 10, padding: "0.8rem 1rem", border: "none", borderBottom: "1px solid var(--border-main)", cursor: "pointer",
                  background: n.read ? "transparent" : "rgba(99,102,241,0.08)" }}>
                <Icon size={18} style={{ color: "#6366f1", flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: n.read ? 400 : 600 }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>{n.body}</div>}
                  {n.from_name && <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}>— {n.from_name}</div>}
                  <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 2 }}>{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            );
          })}
          <button data-testid="notif-see-all" onClick={() => { setOpen(false); navigate("/app/notifications"); }}
            style={{ width: "100%", textAlign: "center", padding: "0.7rem", border: "none", borderTop: "1px solid var(--border-main)", background: "transparent", color: "#6366f1", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 }}>
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}
